import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const u = await login(email, password);
      toast.success("Bienvenido");
      const roleDest = u.role === "admin" ? "/admin" : u.role === "staff" ? "/staff/me" : "/dashboard";
      const dest = from || roleDest;
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2D4FB8 55%, #3B82F6 100%)" }}>
        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at 30% 30%, black 0%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at 30% 30%, black 0%, transparent 75%)",
          }}
        />
        {/* Glow blobs */}
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(96,165,250,0.55) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-32 -right-20 h-[28rem] w-[28rem] rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 60%)" }} />

        {/* Floating booking card mockup */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-[78%] max-w-md">
          <div className="rounded-2xl bg-white/95 backdrop-blur-xl shadow-2xl p-6 border border-white/40">
            <div className="flex items-center justify-between">
              <p className="text-[11px] tracking-[0.2em] uppercase text-slate-500">Próxima cita</p>
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <p className="font-display text-2xl mt-3 text-slate-900">Consulta · 10:30</p>
            <p className="text-sm text-slate-500 mt-1">Con Ana Torres · Jueves 14</p>
            <div className="mt-5 grid grid-cols-5 gap-2">
              {["L","M","M","J","V"].map((d,i) => (
                <div key={d+i} className={`rounded-lg py-2 text-center text-[11px] font-medium ${i===3 ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}>
                  <div>{d}</div>
                  <div className="text-base mt-0.5">{11+i}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3B82F6]" />
              <span>book-it.com.mx/<span className="font-mono font-semibold text-[#1E3A8A]">bookit</span></span>
            </div>
          </div>
          {/* Floating chip */}
          <div className="absolute -top-4 -right-4 rounded-full bg-white shadow-lg px-4 py-2 text-xs font-semibold text-[#1E3A8A] border border-white/60">
            ✓ Confirmada
          </div>
          <div className="absolute -bottom-4 -left-6 rounded-full bg-[#1E3A8A] text-white shadow-lg px-4 py-2 text-xs font-semibold">
            +12 hoy
          </div>
        </div>

        {/* Brand + tagline */}
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/30">
              <span className="font-display text-lg">B</span>
            </div>
            <div>
              <p className="font-display text-lg leading-none">Book-It</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">Smart booking platform</p>
            </div>
          </div>
          <div>
            <p className="overline text-white/70">Bienvenido de vuelta</p>
            <h2 className="font-display text-4xl font-light mt-3 max-w-md leading-tight">
              Vuelve a tu agenda.<br/>
              <span className="text-[#BFDBFE]">Tus clientes te esperan.</span>
            </h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 sm:p-12">
        <Card className="w-full max-w-md border-slate-200">
          <CardContent className="p-8">
            <Link to="/" className="overline" data-testid="login-back-home">← Volver</Link>
            <h1 className="font-display text-3xl font-light mt-4">Iniciar sesión</h1>
            <p className="text-slate-600 mt-2 text-sm">Accede a tu agenda y panel.</p>
            <form className="mt-8 space-y-4" onSubmit={onSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email">Correo</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Contraseña</Label>
                  <Link to="/forgot-password" className="text-xs text-[hsl(var(--accent))] underline" data-testid="login-forgot-link">¿Olvidaste tu contraseña?</Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </div>
              {err && <p className="text-sm text-red-700" data-testid="login-error">{err}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                {loading ? "Entrando…" : "Entrar"}
              </Button>
            </form>
            <p className="text-sm text-slate-600 mt-6">
              ¿Sin cuenta?{" "}
              <Link to="/signup" className="text-primary underline" data-testid="login-go-register">Crear cuenta</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
